import { cva } from 'class-variance-authority'
import { Bot, Contact, ShieldAlert, ShieldCheck, Wallet } from 'lucide-react'

import { AddressDisplaySize } from '~/components/component-library/AddressDisplay/types'
import { SupportedChainId } from '~/constants/chains'
import { useDisplayName } from '~/hooks/useDisplayName'
import { Ens } from '~/icons/Ens'
import { Farcaster } from '~/icons/Farcaster'
import { IAddress } from '~/types'

/**
 * Color hierarchy:
 * 1. Green - internal account
 * 2. Blue - known to you, likely safe
 * 3. Gray - exists, has some info associated
 * 4. Orange - risky, potentially unsafe
 */
const colorMap: Record<AddressDisplayBadgeVariant, ColorStatuses> = {
  // internal
  internal: 'green',
  automation: 'green',
  // known
  contact: 'blue',
  'payee-verified': 'blue',
  // exists
  ens: 'gray',
  farcaster: 'gray',
  // risky
  'payee-unverified': 'orange',
}

type ColorStatuses = 'green' | 'blue' | 'gray' | 'orange' | 'red'

type AddressDisplayBadgeVariant =
  | 'contact'
  | 'farcaster'
  | 'ens'
  | 'payee-unverified'
  | 'payee-verified'
  | 'internal'
  | 'automation'

const buildBadgeClasses = cva(
  'rounded-md select-none ring-1 ring-white dark:ring-black',
  {
    variants: {
      color: {
        gray: 'bg-gray-200 text-secondary dark:bg-gray-700',
        green:
          'bg-green-100 text-green-500 dark:bg-green-900 dark:text-green-400',
        blue: 'bg-blue-100 text-blue-500 dark:bg-blue-900 dark:text-blue-400',
        orange:
          'bg-orange-100 text-orange-500 dark:bg-orange-900 dark:text-orange-400',
        red: 'bg-red-100 text-error dark:bg-red-900 dark:text-red-400',
      },
      size: {
        small: 'p-[3px] [&>svg]:size-2.5',
        base: 'p-1 [&>svg]:size-3',
        large: 'p-1 [&>svg]:size-3.5',
      },
    },
    defaultVariants: {
      size: 'base',
    },
  },
)

export const AddressDisplayBadge = ({
  variant,
  size = 'base',
}: {
  variant: AddressDisplayBadgeVariant
  size?: AddressDisplaySize
}) => {
  let badge = null

  switch (variant) {
    case 'contact':
      badge = <Contact strokeWidth={2.5} />
      break
    case 'farcaster':
      badge = <Farcaster />
      break
    case 'ens':
      badge = <Ens />
      break
    case 'payee-unverified':
      badge = <ShieldAlert strokeWidth={2.5} />
      break
    case 'payee-verified':
      badge = <ShieldCheck strokeWidth={2.5} />
      break
    case 'internal':
      badge = <Wallet strokeWidth={2.5} />
      break
    case 'automation':
      badge = <Bot strokeWidth={2.5} />
      break
    default:
      badge = null
      break
  }

  if (!badge) throw new Error(`Badge not found for variant: ${variant}`)

  const color = colorMap[variant]

  return <div className={buildBadgeClasses({ color, size })}>{badge}</div>
}

export const AddressDisplayBadges = ({
  address,
  chainId,
  showBadges = true,
  size = 'base',
}: {
  address: IAddress
  chainId?: SupportedChainId
  showBadges?: boolean
  size?: AddressDisplaySize
}) => {
  const displayName = useDisplayName(address, chainId)
  const isInContacts = displayName.orgLabel
  const isInternalAccount = displayName.smartAccountName
  const isAutomation = !!displayName.automation

  if (!showBadges) return null

  if (isAutomation) {
    return <AddressDisplayBadge variant="automation" size={size} />
  } else if (isInternalAccount) {
    return <AddressDisplayBadge variant="internal" size={size} />
  } else if (
    displayName.payee?.payeeOrg?.name ||
    displayName.payee?.payeeInvitedEmail
  ) {
    if (displayName.payee?.payeeOrg?.isCompliant) {
      return <AddressDisplayBadge variant="payee-verified" size={size} />
    } else {
      return <AddressDisplayBadge variant="payee-unverified" size={size} />
    }
  } else if (isInContacts) {
    return <AddressDisplayBadge variant="contact" size={size} />
  } else if (displayName.ens) {
    return <AddressDisplayBadge variant="ens" size={size} />
  } else if (
    displayName.farcaster?.displayName ||
    displayName.farcaster?.username
  ) {
    return <AddressDisplayBadge variant="farcaster" size={size} />
  }

  return null
}
